import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import api from "../services/api";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// ─────────────────────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────────────────────

// ✅ FOR TESTING  → REACT_APP_LOGIN_URL=http://192.168.29.213:5555/signin
// ✅ FOR PRODUCTION → REACT_APP_LOGIN_URL=https://portal.sapientcodelabs.xyz/signin
const LOGIN_REDIRECT_URL =
  process.env.REACT_APP_LOGIN_URL || "http://localhost:5555/signin";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:1338/api/";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * STEP A — Read token from URL query param ?token=...
 *
 * The admin app (VerticalSingleMenuItem.js) appends ?token=<jwt>&user=<json>
 * to the chat URL when the user clicks the Chats nav item.
 *
 * We read it here, save it to localStorage so future page loads don't need
 * the URL param, then strip it from the URL bar (clean URL).
 */
const consumeTokenFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("accessToken") || params.get("token");
    const urlRefreshToken = params.get("refreshToken");
    const urlUser = params.get("user");

    if (urlToken) {
      // Persist for future loads (same-tab refreshes, etc.)
      localStorage.setItem("auth_token", urlToken);
      if (urlRefreshToken) {
        localStorage.setItem("refresh_token", urlRefreshToken);
      }

      if (urlUser) {
        try {
          const parsedUser = JSON.parse(decodeURIComponent(urlUser));
          localStorage.setItem("chat_user", JSON.stringify(parsedUser));
        } catch {
          // ignore malformed user param
        }
      }

      // Clean the URL — remove ?token=...&user=... so it doesn't show in address bar
      // and doesn't get bookmarked / shared with the token in it
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      console.log(
        "[Auth] ✓ Token received from URL param and saved to localStorage",
      );
      return urlToken;
    }
  } catch (e) {
    console.warn("[Auth] Failed to read token from URL:", e.message);
  }
  return null;
};

/**
 * STEP B — Read token from localStorage (for subsequent loads after first visit)
 *
 * Checks two locations:
 *   1. localStorage["auth_token"]  — set by consumeTokenFromUrl() above
 *   2. localStorage["admin"]       — Redux-persisted format (fallback)
 *      Shape: { auth: '{"session":{"token":"eyJ..."}}' }
 */
const getStoredToken = () => {
  // 1. Direct key — set by consumeTokenFromUrl on first load
  const direct = localStorage.getItem("auth_token");
  if (direct) return direct;

  // 2. Redux-persisted format (fallback, same-origin only)
  try {
    const raw = localStorage.getItem("admin");
    if (raw) {
      const outer = JSON.parse(raw);
      const authStr = outer?.auth;
      if (authStr) {
        const auth =
          typeof authStr === "string" ? JSON.parse(authStr) : authStr;
        const token = auth?.session?.token;
        if (token) return token;
      }
    }
  } catch {
    // ignore parse errors
  }

  return null;
};

/**
 * Call /auth/validate with optional Bearer token header.
 * withCredentials: true ensures the httpOnly cookie is also sent (production SSO).
 */
const validateSession = async (bearerToken = null) => {
  const headers = {
    "ngrok-skip-browser-warning": "true",
    "Cache-Control": "no-cache",
  };

  if (bearerToken) {
    headers.Authorization = bearerToken.startsWith("Bearer ")
      ? bearerToken
      : `Bearer ${bearerToken}`;
  }

  const response = await api.get(`${API_BASE}auth/validate`, {
    withCredentials: true,
    headers,
  });

  return response; // { success: true, user: {...} }
};

// ─────────────────────────────────────────────────────────────────────────────
// AuthProvider
// ─────────────────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // ── STEP 1: Grab token from URL if present (first visit from admin app) ──
      // Must happen before any API call so the token is available immediately.
      const urlToken = consumeTokenFromUrl();

      // ── STEP 2: Get best available token (URL → localStorage → null) ─────────
      const token = urlToken || getStoredToken();

      let response = null;

      if (token) {
        console.log("[Auth] Validating session with token...");
        try {
          response = await validateSession(token);
        } catch (tokenErr) {
          const status = tokenErr?.response?.status;
          const errMsg =
            tokenErr?.response?.data?.error?.message || tokenErr.message;

          if (status === 401) {
            console.log(
              "[Auth] Access token expired or invalid. Attempting refresh...",
            );
            try {
              const refreshResponse = await api.post(
                `${API_BASE}auth/refresh`,
                { refreshToken: localStorage.getItem("refresh_token") },
                { withCredentials: true },
              );
              const newAccessToken = refreshResponse.data?.accessToken;
              const newRefreshToken = refreshResponse.data?.refreshToken;
              if (newAccessToken) {
                localStorage.setItem("auth_token", newAccessToken);
                if (newRefreshToken) {
                  localStorage.setItem("refresh_token", newRefreshToken);
                }
                response = await validateSession(newAccessToken);
                console.log("[Auth] Token refreshed successfully");
              } else {
                throw new Error("No access token in refresh response");
              }
            } catch (refreshErr) {
              console.warn(
                "[Auth] Refresh failed:",
                refreshErr?.response?.status,
              );
              localStorage.removeItem("auth_token");
              localStorage.removeItem("refresh_token");
            }
          } else {
            console.warn("[Auth] Token validation failed:", status, errMsg);
            localStorage.removeItem("auth_token");
          }
        }
      }

      // ── STEP 4: Process result ────────────────────────────────────────────────
      if (response?.success && response?.user) {
        const userData = response.user;
        const resolvedUser = {
          ...userData,
          role: userData.user_role?.role || userData.role,
        };
        setUser(resolvedUser);
        localStorage.setItem("user", JSON.stringify(resolvedUser));
        console.log(
          "[Auth] ✓ Authenticated as:",
          resolvedUser.name || resolvedUser.email,
        );
        // alert("Access token refreshed successfully!");
      } else {
        // ── STEP 5: All attempts failed → show the in-app login page ──────────
        // Previously this redirected to the portal signin URL. Now we leave
        // `user` null so App renders the in-app <SignInPage /> instead of
        // bouncing over to portal.sapientcodelabs just to log in.
        console.warn("[Auth] All auth attempts failed. Showing in-app login.");
        setUser(null);
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (userData) => {
    const resolvedUser = {
      ...userData,
      role: userData.user_role?.role || userData.role,
    };
    setUser(resolvedUser);
    localStorage.setItem("user", JSON.stringify(resolvedUser));
  };

  const logout = async () => {
    try {
      const api = require("../services/api").default;
      await api.post("/auth/logout");
    } catch (e) {
      console.error("[Auth] Logout API failed:", e.message);
    }
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("chat_user");
    localStorage.removeItem("admin");
    // Reload to the app root — with no token, AuthContext lands on the in-app
    // <SignInPage /> instead of redirecting out to the portal.
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
